import React, { Fragment } from 'react';
import { Stack } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

const inputStyle = {
  fontSize: '16px',
  my: '8px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#BDBDBD',
  },
  '& .MuiInputBase-input': { padding: '8px 14px' },
  '& input::placeholder': {
    fontSize: { xs: '10px', sm: '14px' },
    fontWeight: 500,
    color: '#CCD1D9',
    opacity: 1,
  },
  backgroundColor: 'primary.light',
  width: '100%',
};

const colorRequired = {
  color: '#ED5565',
  fontSize: '14px',
  fontWeight: '500',
};

type GenerateFieldChildType = {
  field: any;
  answer: any;
  labelParent: string;
  disabled: boolean;
  handleChildChangeValue: (name: string, value: any) => void;
  handleChildDFChangeValue: (name: string, value: any) => void;
  handleChildMultiChangeValue: (name: string, value: any) => void;
  handleChildMultiDFChangeValue: (name: string, value: any) => void;
};

const GenerateFieldChild: React.FC<GenerateFieldChildType> = (props) => {
  const field = props.field;
  const [disable, setDisable] = useState(true);

  const handleRadioChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    name: string
  ) => {
    const { value } = event.target;
    props.handleChildChangeValue(name, value);
  };

  const handleSelectChange = (event: any, name: string) => {
    const { value } = event.target;
    props.handleChildChangeValue(name, value);
  };

  const handleInputChange = (event: any) => {
    props.handleChildChangeValue(event.target.name, event.target.value);
  };

  const handleCheckboxChange = (event: any, name: string) => {
    const { value, checked } = event.target;
    let tempAnswer: any = [];
    if (!props.answer?.children_answers) {
      tempAnswer = [value];
    } else {
      if (checked) {
        tempAnswer = [
          ...props.answer?.children_answers[field.field_id]?.value,
          ...[value],
        ];
      } else {
        if (value === 'その他') {
          tempAnswer = props.answer?.children_answers[
            field.field_id
          ]?.value.filter(
            (val: any) =>
              val !== 'その他' &&
              !val.startsWith('その他（') &&
              !val.startsWith('その他:')
          );
        } else {
          tempAnswer = props.answer?.children_answers[
            field.field_id
          ]?.value.filter((val: any) => val !== value);
        }
      }
    }
    props.handleChildMultiChangeValue(name, tempAnswer);
  };

  const handleCheckboxDFChange = (event: any, name: string) => {
    const { value, checked } = event.target;
    let tempAnswer: any = [];
    if (props.answer && props.answer?.children_answers) {
      if (!props.answer?.children_answers[field.field_id]?.dependent_answers) {
        tempAnswer = [value];
      } else {
        if (checked) {
          tempAnswer = [
            ...props.answer?.children_answers[field.field_id]
              ?.dependent_answers,
            ...[value],
          ];
        } else {
          if (value === 'その他') {
            tempAnswer = props.answer?.children_answers[
              field.field_id
            ]?.dependent_answers.filter(
              (val: any) =>
                val !== 'その他' &&
                !val.startsWith('その他（') &&
                !val.startsWith('その他:')
            );
          } else {
            tempAnswer = props.answer?.children_answers[
              field.field_id
            ]?.dependent_answers.filter((val: any) => val !== value);
          }
        }
      }
    } else {
      tempAnswer = [value];
    }
    props.handleChildMultiDFChangeValue(name, tempAnswer);
  };

  const handleInputDFChange = (event: any) => {
    const { name, value } = event.target;
    props.handleChildDFChangeValue(name, value);
  };

  const handleSelectDFChange = (event: any, name: string) => {
    const { value } = event.target;
    props.handleChildDFChangeValue(name, value);
  };

  const handleSelectDFYearChange = (event: any, name: string) => {
    const { value } = event.target;
    let result = `${value}-${dayjs().month() + 1}`;
    if (
      props.answer &&
      props.answer?.children_answers &&
      props.answer?.children_answers[field.field_id] &&
      props.answer?.children_answers[field.field_id]?.dependent_answers
    ) {
      const depAns =
        props.answer?.children_answers[
          field.field_id
        ]?.dependent_answers[0].split('-');
      result = `${value}-${depAns[1] ? depAns[1] : dayjs().month() + 1}`;
    }

    props.handleChildDFChangeValue(name, result);
  };

  const handleSelectDFMonthChange = (event: any, name: string) => {
    const { value } = event.target;
    let result = `${dayjs().year()}-${value}`;
    if (
      props.answer &&
      props.answer?.children_answers &&
      props.answer?.children_answers[field.field_id] &&
      props.answer?.children_answers[field.field_id]?.dependent_answers
    ) {
      const depAns =
        props.answer?.children_answers[
          field.field_id
        ]?.dependent_answers[0].split('-');
      result = `${depAns[0] ? depAns[0] : dayjs().year()}-${value}`;
    }

    props.handleChildDFChangeValue(name, result);
  };

  const handleInputForCheckboxChange = (event: any) => {
    const { name, value } = event.target;
    let tempAnswer = {};
    if (!props.answer?.children_answers[field.field_id].dependent_answers) {
      tempAnswer = [
        {
          [name]: value,
        },
      ];
    } else {
      tempAnswer = [
        {
          ...props.answer?.children_answers[field.field_id]
            .dependent_answers[0],
          [name]: value,
        },
      ];
    }

    props.handleChildMultiDFChangeValue(field.field_id, tempAnswer);
  };

  useEffect(() => {
    setDisable(true);
    if (props.answer) {
      if (props.answer['value'][0] === props.labelParent) {
        setDisable(false);
      }
    }
  }, [props.answer, props.labelParent]);

  switch (field.type) {
    case 'radio':
      return (
        <Box my={'5px'} ml={'20px'} sx={{ display: 'block' }}>
          <Typography fontWeight={700}>
            {field.label}
            {field.required && (
              <Typography component='label' {...colorRequired}>
                *
              </Typography>
            )}
          </Typography>
          <RadioGroup
            // row
            aria-labelledby='demo-controlled-radio-buttons-group'
            name={field.field_id}
            value={
              props.answer?.children_answers &&
              props.answer?.children_answers[field.field_id]
                ? props.answer?.children_answers[field.field_id]?.value
                : ''
            }
            onChange={(e) => handleRadioChange(e, field.field_id)}
          >
            {field.options.map((option: any, idx: number) => {
              return (
                <FormControlLabel
                  key={idx}
                  value={option}
                  control={<Radio disabled={disable || props.disabled} />}
                  label={option}
                />
              );
            })}
          </RadioGroup>
        </Box>
      );
    case 'select':
      return (
        <Stack
          direction='column'
          spacing={'8px'}
          justifyContent={'flex-start'}
          my={'8px'}
          ml={'20px'}
          sx={{ display: 'flex' }}
        >
          <Stack
            direction='row'
            spacing={'8px'}
            justifyContent={'flex-start'}
            alignItems={'center'}
            my={'8px'}
            ml={'20px'}
            sx={{ display: 'flex' }}
          >
            {field.label && (
              <Typography width={'100px'}>
                {field.label}
                {field.required && (
                  <Typography component='label' {...colorRequired}>
                    *
                  </Typography>
                )}
              </Typography>
            )}

            <Select
              value={
                props.answer?.children_answers &&
                props.answer?.children_answers[field.field_id]
                  ? props.answer?.children_answers[field.field_id]?.value.toString().startsWith('その他（') 
                    ? 'その他' 
                    : props.answer?.children_answers[field.field_id]?.value
                  : ''
              }
              disabled={disable || props.disabled}
              onChange={(e) => handleSelectChange(e, field.field_id)}
              sx={{
                backgroundColor: 'primary.light',
                width: '100%',
                '& .MuiInputBase-input.MuiOutlinedInput-input': {
                  p: '6px 14px',
                },
              }}
            >
              {field.options.map((option: any, idx: number) => {
                return (
                  <MenuItem value={option} key={idx}>
                    {option}
                  </MenuItem>
                );
              })}
            </Select>
          </Stack>
          {props.answer?.children_answers &&
            props.answer?.children_answers[field.field_id] &&
            props.answer?.children_answers[field.field_id]?.value.toString().startsWith('その他') && (
              <OutlinedInput
                placeholder='その他の内容を入力してください'
                sx={inputStyle}
                value={
                  props.answer?.children_answers[field.field_id]?.value.toString().startsWith('その他（')
                    ? props.answer?.children_answers[field.field_id]?.value.toString().replace('その他（', '').replace('）', '')
                    : ''
                }
                onChange={(e) => {
                  const value = e.target.value;
                  props.handleChildChangeValue(field.field_id, `その他（${value}）`);
                }}
                disabled={disable || props.disabled}
              />
          )}
        </Stack>
      );
    case 'text':
      return (
        <>
          <Stack
            direction='row'
            spacing={'8px'}
            justifyContent={'flex-start'}
            alignItems={'center'}
            my={'8px'}
            ml={'20px'}
            sx={{ display: 'flex' }}
          >
            <Typography width={'100px'}>
              {field.label || '-'}
              {field.required && (
                <Typography component='label' {...colorRequired}>
                  *
                </Typography>
              )}
            </Typography>
            <Stack sx={{ width: '100%' }}>
              <OutlinedInput
                placeholder={`${field.label}を入力してください`}
                sx={inputStyle}
                inputProps={{
                  sx: {
                    backgroundColor: 'primary.light',
                  },
                }}
                disabled={disable || props.disabled}
                name={field.field_id}
                value={
                  props.answer?.children_answers &&
                  props.answer?.children_answers[field.field_id]
                    ? props.answer?.children_answers[field.field_id]?.value
                    : ''
                }
                onChange={(event) => handleInputChange(event)}
              />
              <Typography fontSize={'12px'} color={'primary.dark'}>
                {field.help_text || ''}
              </Typography>
            </Stack>
          </Stack>
          {field.dependent_fields &&
            Array.isArray(field.dependent_fields) &&
            field.dependent_fields.map((df: any, index: number) => {
              if (df.type === 'text') {
                return (
                  <Stack
                    key={index}
                    direction='row'
                    spacing={'8px'}
                    justifyContent={'flex-start'}
                    alignItems={'center'}
                    my={'8px'}
                    ml={'20px'}
                    sx={{ display: 'flex' }}
                  >
                    <Typography width={'100px'}>{df.label}</Typography>
                    <Stack sx={{ width: '100%' }}>
                      <OutlinedInput
                        placeholder={`${df.label}を入力してください`}
                        sx={inputStyle}
                        inputProps={{
                          sx: {
                            backgroundColor: 'primary.light',
                          },
                        }}
                        disabled={disable || props.disabled}
                        name={field.field_id}
                        value={
                          props.answer?.children_answers &&
                          props.answer?.children_answers[field.field_id]
                            ? props.answer?.children_answers[field.field_id]
                                ?.dependent_answers
                            : ''
                        }
                        onChange={(event) => handleInputDFChange(event)}
                      />
                      <Typography fontSize={'12px'} color={'primary.dark'}>
                        {df.help_text || ''}
                      </Typography>
                    </Stack>
                  </Stack>
                );
              }
              if (df.type === 'select') {
                return (
                  <Stack
                    key={index}
                    direction='row'
                    spacing={'8px'}
                    justifyContent={'flex-start'}
                    alignItems={'center'}
                    my={'8px'}
                    ml={'20px'}
                    sx={{ display: 'flex' }}
                  >
                    <Typography width={'90px'}>{df.label}</Typography>
                    <Select
                      value={
                        props.answer?.children_answers &&
                        props.answer?.children_answers[field.field_id]
                          ? props.answer?.children_answers[field.field_id]
                              ?.dependent_answers
                          : ''
                      }
                      onChange={(e) => handleSelectDFChange(e, field.field_id)}
                      sx={{
                        backgroundColor: 'primary.light',
                        width: '150px',
                        '& .MuiInputBase-input.MuiOutlinedInput-input': {
                          p: '6px 14px',
                        },
                      }}
                      disabled={disable || props.disabled}
                    >
                      {df.options.map((option: any, idx: number) => {
                        return (
                          <MenuItem value={option} key={idx}>
                            {option}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </Stack>
                );
              }
              if (df.type === 'year_month') {
                return (
                  <Stack
                    key={index}
                    direction='row'
                    spacing={'8px'}
                    justifyContent={'flex-start'}
                    alignItems={'center'}
                    width={'100%'}
                    my={'8px'}
                    ml={'20px'}
                    sx={{ display: 'flex' }}
                  >
                    <Typography width={'100px'}>{df.label}</Typography>
                    <Grid container sx={{ width: { xs: '130px', sm: 'auto' } }}>
                      <Grid item xs={12} sm={4}>
                        <Stack
                          key={index}
                          direction='row'
                          spacing={'8px'}
                          alignItems={'center'}
                          mr={'8px'}
                          my={'8px'}
                        >
                          <Select
                            value={
                              props.answer &&
                              props.answer?.children_answers &&
                              props.answer?.children_answers[field.field_id] &&
                              props.answer?.children_answers[field.field_id]
                                ?.dependent_answers
                                ? props.answer?.children_answers[
                                    field.field_id
                                  ]?.dependent_answers[0].split('-')[0]
                                : ''
                            }
                            name='year'
                            onChange={(e) =>
                              handleSelectDFYearChange(e, field.field_id)
                            }
                            sx={{
                              backgroundColor: 'primary.light',
                              width: '130px',
                              '& .MuiInputBase-input.MuiOutlinedInput-input': {
                                p: '6px 14px',
                              },
                            }}
                            disabled={disable || props.disabled}
                          >
                            {Array.from({ length: 20 }).map(
                              (option: any, idx: number) => {
                                const year =
                                  new Date().getFullYear() - 19 + idx;
                                return (
                                  <MenuItem value={year} key={idx}>
                                    {year}
                                  </MenuItem>
                                );
                              }
                            )}
                          </Select>
                          <Typography>年</Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Stack
                          key={index}
                          direction='row'
                          spacing={'8px'}
                          alignItems={'center'}
                          mr={'8px'}
                          my={'8px'}
                        >
                          <Select
                            value={
                              props.answer &&
                              props.answer?.children_answers &&
                              props.answer?.children_answers[field.field_id] &&
                              props.answer?.children_answers[field.field_id]
                                ?.dependent_answers
                                ? props.answer?.children_answers[
                                    field.field_id
                                  ]?.dependent_answers[0].split('-')[1]
                                : ''
                            }
                            name='month'
                            onChange={(e) =>
                              handleSelectDFMonthChange(e, field.field_id)
                            }
                            sx={{
                              backgroundColor: 'primary.light',
                              width: '130px',
                              '& .MuiInputBase-input.MuiOutlinedInput-input': {
                                p: '6px 14px',
                              },
                            }}
                            disabled={disable || props.disabled}
                          >
                            {Array.from({ length: 12 }).map(
                              (option: any, idx: number) => {
                                return (
                                  <MenuItem value={idx + 1} key={idx}>
                                    {idx + 1}
                                  </MenuItem>
                                );
                              }
                            )}
                          </Select>
                          <Typography>月</Typography>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Stack>
                );
              }
              return <></>;
            })}
        </>
      );

    case 'checkbox':
      return (
        <>
          <Box my={'5px'} ml={'20px'} sx={{ display: 'block' }}>
            <Typography fontWeight={700}>
              {field.label || ''}
              {field.required && (
                <Typography component='label' {...colorRequired}>
                  *
                </Typography>
              )}
            </Typography>
            <Grid container spacing={'8px'}>
              {field.options.map((option: any, idx: number) => {
                const isOtherOption = option === 'その他';
                // Ensure we have an array, even if empty
                const answerValues =
                  props.answer?.children_answers?.[field.field_id]?.value || [];
                const otherValue = answerValues.find((val: string) =>
                  val?.startsWith('その他（')
                );
                const otherInputValue = otherValue
                  ? otherValue.substring(4, otherValue.length - 1)
                  : '';
                const isOtherChecked = answerValues.some(
                  (val: string) =>
                    val === 'その他' || val?.startsWith('その他（')
                );
                const otherMoreValue = answerValues.find((val: string) =>
                  val?.startsWith('other_more_information:')
                );
                const otherMoreInputValue = otherMoreValue
                  ? otherMoreValue.substring('other_more_information: '.length)
                  : '';

                return (
                  <Fragment key={`${field.field_id}-${idx}`}>
                    <Grid
                      item
                      xs={
                        field.dependent_fields &&
                        field.dependent_fields.length > 0 &&
                        field.dependent_fields[0].type === 'text'
                          ? 12
                          : 6
                      }
                      sm={
                        field.dependent_fields &&
                        field.dependent_fields.length > 0 &&
                        field.dependent_fields[0].type === 'text'
                          ? 12
                          : 4
                      }
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              isOtherOption
                                ? Boolean(isOtherChecked)
                                : Boolean(answerValues.includes(option))
                            }
                            onChange={(event) =>
                              handleCheckboxChange(event, field.field_id)
                            }
                            value={option}
                            disabled={disable || props.disabled}
                          />
                        }
                        label={option}
                        sx={{
                          '& .MuiSvgIcon-root': { fontSize: '18px' },
                          '& .MuiTypography-root': {
                            fontSize: '14px',
                            wordBreak: 'break-word',
                          },
                        }}
                      />
                      {isOtherOption &&
                        isOtherChecked &&
                        field.dependent_fields &&
                        field.dependent_fields.length > 0 &&
                        field.dependent_fields[0].type === 'text' && (
                          <OutlinedInput
                            placeholder='その他の内容を入力してください'
                            sx={{
                              ...inputStyle,
                              width: 'calc(100% - 85px)',
                              my: '8px',
                            }}
                            value={otherMoreInputValue || ''}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              let updatedValues = [...(answerValues || [])];

                              // Remove any existing その他 entries
                              updatedValues = updatedValues.filter(
                                (val: string) =>
                                  val !== 'other_more_information' &&
                                  !val?.startsWith('other_more_information:')
                              );

                              // Add the new その他 entry with input value
                              updatedValues.push(
                                `other_more_information: ${newValue}`
                              );

                              props.handleChildMultiChangeValue(
                                field.field_id,
                                updatedValues
                              );
                            }}
                            disabled={disable || props.disabled}
                          />
                        )}
                    </Grid>
                    {
                    field.dependent_fields &&
                    field.dependent_fields.length > 0 &&
                      field.dependent_fields[0].type === 'text' && (
                        <Grid item xs={12}>
                          {props.answer?.children_answers &&
                            props.answer?.children_answers[field.field_id] &&
                            Array.isArray(
                              props.answer?.children_answers[field.field_id]
                                .value
                            ) &&
                            props.answer?.children_answers[
                              field.field_id
                            ].value.map((val: any, index: number) => {
                              if (option !== val)
                                return <Fragment key={index}></Fragment>;
                              return (
                                <Stack key={index}>
                                  <Stack
                                    key={index}
                                    direction='row'
                                    spacing={'8px'}
                                    justifyContent={'flex-start'}
                                    alignItems={'center'}
                                    width={'100%'}
                                    my={'8px'}
                                  >
                                    <Typography
                                      width={'100px'}
                                      sx={{
                                        fontSize: { xs: '12px', sm: '14px' },
                                      }}
                                    >
                                      {field.dependent_fields &&
                                      field.dependent_fields.length > 0 &&
                                        field.dependent_fields[0].label}
                                    </Typography>
                                    <OutlinedInput
                                      placeholder={`${
                                        field.dependent_fields[0].label || ''
                                      }を入力してください`}
                                      sx={inputStyle}
                                      inputProps={{
                                        sx: {
                                          backgroundColor: 'primary.light',
                                        },
                                      }}
                                      disabled={disable || props.disabled}
                                      name={val}
                                      value={
                                        props.answer?.children_answers &&
                                        props.answer?.children_answers[
                                          field.field_id
                                        ] &&
                                        props.answer?.children_answers[
                                          field.field_id
                                        ].dependent_answers &&
                                        props.answer?.children_answers[
                                          field.field_id
                                        ].dependent_answers.length > 0 &&
                                        props.answer?.children_answers[
                                          field.field_id
                                        ].dependent_answers[0][val] !==
                                          undefined
                                          ? props.answer?.children_answers[
                                              field.field_id
                                            ].dependent_answers[0][val]
                                          : ''
                                      }
                                      onChange={(event) =>
                                        handleInputForCheckboxChange(event)
                                      }
                                    />
                                  </Stack>
                                </Stack>
                              );
                            })}
                        </Grid>
                      )}

                    {isOtherOption &&
                      isOtherChecked &&
                      field.dependent_fields &&
                      field.dependent_fields[0]?.type !== 'text' && (
                        <Grid item xs={12}>
                          <OutlinedInput
                            placeholder='その他の内容を入力してください'
                            sx={{
                              ...inputStyle,
                              width: '100%',
                            }}
                            value={otherInputValue || ''}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              let updatedValues = [...(answerValues || [])];

                              // Remove any existing その他 entries
                              updatedValues = updatedValues.filter(
                                (val: string) =>
                                  val !== 'その他' &&
                                  !val?.startsWith('その他（') &&
                                  !val?.startsWith('その他:')
                              );

                              // Add the new その他 entry with input value
                              updatedValues.push(`その他（${newValue}）`);

                              props.handleChildMultiChangeValue(
                                field.field_id,
                                updatedValues
                              );
                            }}
                            disabled={disable || props.disabled}
                          />
                        </Grid>
                      )}
                  </Fragment>
                );
              })}
            </Grid>
          </Box>
          {field.dependent_fields &&
          field.dependent_fields.length > 0 &&
            field.dependent_fields[0].type === 'checkbox' && (
              <Box my={'5px'} ml={'20px'} sx={{ display: 'block' }}>
                <Typography fontWeight={700}>
                  {field.dependent_fields[0].label || ''}
                </Typography>
                <Grid container spacing={'8px'}>
                  {field.dependent_fields[0].options.map(
                    (option: any, idx: number) => {
                      const isOtherOption = option === 'その他';
                      // Ensure we have valid arrays
                      const dependentAnswers =
                        props.answer?.children_answers?.[field.field_id]
                          ?.dependent_answers || [];

                      const otherValue = dependentAnswers.find((val: string) =>
                        val?.startsWith('その他（')
                      );
                      const otherInputValue = otherValue
                        ? otherValue.substring(4, otherValue.length - 1)
                        : '';
                      const isOtherChecked = dependentAnswers.some(
                        (val: string) =>
                          val === 'その他' || val?.startsWith('その他（')
                      );

                      return (
                        <Fragment key={`${field.field_id}-df-${idx}`}>
                          <Grid item xs={6} sm={4}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={
                                    isOtherOption
                                      ? Boolean(isOtherChecked)
                                      : Boolean(
                                          dependentAnswers.includes(option)
                                        )
                                  }
                                  onChange={(event) =>
                                    handleCheckboxDFChange(
                                      event,
                                      field.field_id
                                    )
                                  }
                                  value={option}
                                  disabled={disable || props.disabled}
                                />
                              }
                              label={option}
                              sx={{
                                '& .MuiSvgIcon-root': { fontSize: '18px' },
                                '& .MuiTypography-root': {
                                  fontSize: '14px',
                                  wordBreak: 'break-word',
                                },
                              }}
                            />
                          </Grid>
                          {isOtherOption && isOtherChecked && (
                            <Grid item xs={12}>
                              <OutlinedInput
                                placeholder='その他の内容を入力してください'
                                sx={{
                                  ...inputStyle,
                                  width: '100%',
                                }}
                                value={otherInputValue || ''}
                                onChange={(e) => {
                                  const newValue = e.target.value;
                                  let updatedValues = [
                                    ...(dependentAnswers || []),
                                  ];

                                  // Remove any existing その他 entries
                                  updatedValues = updatedValues.filter(
                                    (val: string) =>
                                      val !== 'その他' &&
                                      !val?.startsWith('その他（') &&
                                      !val?.startsWith('その他:')
                                  );

                                  // Add the new その他 entry with input value
                                  updatedValues.push(`その他（${newValue}）`);

                                  props.handleChildMultiDFChangeValue(
                                    field.field_id,
                                    updatedValues
                                  );
                                }}
                                disabled={disable || props.disabled}
                              />
                            </Grid>
                          )}
                        </Fragment>
                      );
                    }
                  )}
                </Grid>
              </Box>
            )}
        </>
      );
    default:
      return <Box my={'5px'}></Box>;
  }
};

export default GenerateFieldChild;
